import type { ChangeEvent } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatFileName } from '../../../helper/UtilityFunctions';
import UserService from '../../../services/User.service';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectFiles, setFiles } from '../../../store/reducers';
import { Button, Headline, Loading, Modal } from '../../atoms';
import { DocumentBox } from '../../molecules/DocumentBox/DocumentBox';
import { Header } from '../../organisms';
import { DocumentPreview } from '../../organisms/DocumentPreview/DocumentPreview';
import classes from './Documents.module.scss';

const validFileExtensions = [
  'jpg',
  'jpeg',
  'png',
  'webp',
  'heic',
  'heif',
  'pdf',
  'doc',
  'docx',
  'odt',
  'xls',
  'xlsx',
  'ods',
];
const maxUploadBytes = 25 * 1024 * 1024;

export function Documents() {
  const files = useAppSelector(selectFiles);
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);

  const [showUploadError, setShowUploadError] = useState(false);
  const [uploadFileName, setUploadFileName] = useState('');
  const [showUploadFailure, setShowUploadFailure] = useState(false);
  const [showUploadSizeLimit, setShowUploadSizeLimit] = useState(false);
  const [showExistingDocumentModalVisible, setShowExistingDocumentModalVisible] = useState(false);
  const [fileInputValue, setFileInputValue] = useState('');

  const [documentUri, setDocumentUri] = useState('');
  const [viewDocument, setViewDocument] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [documentName, setDocumentName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getUserFiles = async () => {
    setLoading(true);
    const fileResponse = await UserService.getFiles();
    if (fileResponse.error) {
      console.error(fileResponse);
    }
    if (!fileResponse.error) {
      dispatch(setFiles(fileResponse.data));
    }
    setLoading(false);
  };

  const onChangeFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    const normalizedName = selectedFile.name.toLowerCase();
    const validFile = validFileExtensions.some((extension) => normalizedName.endsWith(`.${extension}`));

    if (!validFile) {
      setUploadFileName(selectedFile.name);
      setShowUploadError(true);
      setFileInputValue('');

      return;
    }

    setShowUploadError(false);

    if (event.target.files!.length) {
      if (selectedFile.size > maxUploadBytes) {
        setUploadFileName(selectedFile.name);
        setShowUploadSizeLimit(true);
        setFileInputValue('');

        return;
      }

      const validFileRegex =
        // eslint-disable-next-line no-useless-escape
        /^(?=[a-z0-9\_\-])[a-z0-9\_\-]+[\\s]{0}[\.]{1}[a-z]+$/i;
      let renamedFile;
      if (!validFileRegex.test(selectedFile.name)) {
        renamedFile = new File([selectedFile], formatFileName(selectedFile.name));
      }

      let isFound = false;
      for (let i = 0; i < files.length; i++) {
        if (renamedFile) {
          if (files[i].filename === renamedFile.name) {
            isFound = true;
            break;
          }
        } else if (files[i].filename === selectedFile.name) {
          isFound = true;
          break;
        }
      }
      if (isFound) {
        setShowExistingDocumentModalVisible(true);
        setFileInputValue('');

        return;
      }
      setLoading(true);

      const formData = new FormData();
      const file = renamedFile || selectedFile;

      formData.append('file', file);

      const uploadResponse = await UserService.uploadFile(formData);
      if (uploadResponse.error) {
        console.error(uploadResponse);
        setUploadFileName(file.name);
        setShowUploadFailure(true);
        setLoading(false);
        setFileInputValue('');

        return;
      }

      getUserFiles();
    }
  };

  const openDocument = (uri: string, name: string) => {
    const fileExtensions = ['pdf', 'jpg', 'jpeg', 'png'];
    const fileExt = fileExtensions.find((ext) => name.endsWith(ext));

    if (fileExt) {
      setDocumentUri(uri);
      setViewDocument(true);
      setDocumentType(fileExt);
      setDocumentName(name);
    }
  };

  useEffect(() => {
    getUserFiles();
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <React.Fragment>
      {viewDocument && (
        <DocumentPreview uri={documentUri} type={documentType} name={documentName} setViewDocument={setViewDocument} />
      )}
      <Header />
      <Modal
        modalVisible={showUploadError}
        setModalVisible={setShowUploadError}
        cb={() => setShowUploadError(false)}
        disableSecondaryButton={true}
        primaryButtonText='Schließen'
      >
        <div>Fehler beim Upload von</div>
        <div>{uploadFileName}</div>
        <div>Erlaubte Dateiendungen:</div>
        <div>.jpg, .jpeg, .png, .webp, .heic, .heif, .pdf, .doc, .docx, .odt, .xls, .xlsx, .ods</div>
      </Modal>
      <Modal
        modalVisible={showUploadFailure}
        setModalVisible={setShowUploadFailure}
        cb={() => setShowUploadFailure(false)}
        disableSecondaryButton={true}
        primaryButtonText='Schließen'
      >
        <div>Upload fehlgeschlagen</div>
        <div>{uploadFileName}</div>
        <div>Bitte versuchen Sie es erneut.</div>
      </Modal>
      <Modal
        modalVisible={showUploadSizeLimit}
        setModalVisible={setShowUploadSizeLimit}
        cb={() => setShowUploadSizeLimit(false)}
        disableSecondaryButton={true}
        primaryButtonText='Schließen'
      >
        <div>Upload fehlgeschlagen</div>
        <div>{uploadFileName}</div>
        <div>Maximale Dateigröße: 25 MB.</div>
      </Modal>
      <Modal
        modalVisible={showExistingDocumentModalVisible}
        setModalVisible={setShowExistingDocumentModalVisible}
        cb={() => setShowExistingDocumentModalVisible(false)}
        disableSecondaryButton={true}
        primaryButtonText='Schließen'
      >
        <Headline level={3}>Ungültiger Dokumentenname</Headline>
        <div>Sie haben bereits ein Dokument mit diesem Namen hochgeladen. Bitte benennen Sie das Dokument um.</div>
      </Modal>
      <div className={classes.Documents}>
        <div className={classes.container}>
          <div className={classes.description}>
            <Headline level={3}>Meine Dokumente</Headline>
            <p>Hier können Sie Ihre medizinischen Dokumente hochladen.</p>
            <p>Dies können zum Beispiel Arztbriefe, Medikamentenpläne, Entlassbriefe etc. sein.</p>
          </div>
          <div className={classes.documents}>
            {files.length > 0 ? (
              files.map((file, index) => {
                if (file.type === 'other') {
                  return (
                    <DocumentBox
                      filetype='other'
                      key={index}
                      id={file.id}
                      filename={file.filename}
                      thumb={file.thumbnail}
                      reload={getUserFiles}
                      openDocument={openDocument}
                      setLoading={setLoading}
                    />
                  );
                }

                return null;
              })
            ) : (
              <div className={classes.noDocumentsUploaded}>Sie haben noch keine Dokumente übermittelt.</div>
            )}
          </div>
          <div className={classes['upload-button-container']}>
            <input
              ref={fileInputRef}
              type='file'
              onChange={onChangeFile}
              id='file-upload'
              accept='.pdf, image/*, .doc, .docx, .odt, .xls, .xlsx, .ods, .heic, .heif, .webp'
              style={{ display: 'none' }}
              value={fileInputValue}
            />
            <Button onClick={() => fileInputRef.current?.click()}>Dokument hinzufügen</Button>
          </div>
          <div className={classes['back-to-dashboard-container']}>
            <Link className={classes['back-to-dashboard-btn']} to='/dashboard'>
              <div>Zurück zum Dashboard</div>
            </Link>
          </div>
          {files.length > 0 && (
            <div className={classes.prescriptions}>
              {files.some((file) => file.type === 'prescription') && <Headline level={3}>Meine Rezepte</Headline>}
              {files.map((file, index) => {
                if (file.type !== 'other') {
                  return (
                    <DocumentBox
                      filetype='prescription'
                      fileCreated={file.created}
                      key={index}
                      id={file.id}
                      filename={file.filename}
                      thumb={file.thumbnail}
                      reload={getUserFiles}
                      openDocument={openDocument}
                      setLoading={setLoading}
                    />
                  );
                }

                return null;
              })}
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}
